// React hooks
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

// Helpers i API servisi
import { getPermissionCategoriesAPI, getPermissionsAPI, getUserPermissionsAPI } from "services/api/users/api";
import { PermissionAssignmentFilter, PermissionCategory, PermissionWithAssignment } from "services/api/users/types";


// Navigacija


// Tipovi i konstante
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";

import {
  UserPermissionSearchPageCmp,
  UserPermissionSearchPageProps,
} from "./types";
import { USER_PERMISSION_SEARCH_PAGE_TITLE } from "ui/components/PageTitle/consts";

// UI komponente
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { NoResults } from "ui/components/NoResults";

// Moduli specifični za projekt
import { Routes } from "ui/modules/users/navigation";
import TabMenu from "ui/modules/users/pages/components/TabMenu/TabMenu";

// Lokalne komponente
import {
  UserPermissionSearchFormValues,
} from "services/api/users/types";
import UserPermissionSearchFilters from "./components/UserPermissionSearchFilters/UserPermissionSearchFilters";
import UserPermissionSearchResults from "./components/UserPermissionSearchResults/UserPermissionSearchResults";
import { UserPermissionBulkEditModal } from "./components/UserPermissionBulkEditModal";

const UserPermissionSearchPage: UserPermissionSearchPageCmp = (
  props: UserPermissionSearchPageProps
) => {
  const { id } = useParams();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<PermissionWithAssignment>
  >(initialDataWithPagination);

  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [selectedPermissionsMap, setSelectedPermissionsMap] = useState<Map<string, PermissionWithAssignment>>(new Map());
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showBulkEditModal, setShowBulkEditModal] = useState<boolean>(false);
  const [categories, setCategories] = useState<PermissionCategory[]>([]);


  useEffect(() => {
    if (id) {
      fetchSearchResult(searchResults.pagination);
      fetchCategories();
    }
  }, []);

  const fetchCategories = async () => {
    try {
      const categoriesData = await getPermissionCategoriesAPI();
      setCategories(categoriesData);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchSearchResult = async (
    pagination: Pagination,
    name?: string | null,
    categoryIds?: string[],
    includeSubcategories?: boolean,
    assignmentFilter?: PermissionAssignmentFilter
  ) => {
    setIsLoading(true);
    try {
      if (id) {
        const [allPermissions, userPermissions] = await Promise.all([
          getPermissionsAPI(pagination, name, categoryIds, includeSubcategories),
          getUserPermissionsAPI(id)
        ]);

        const userPermissionIds = new Set(userPermissions.map(up => up.id));

        let combinedData: PermissionWithAssignment[] = allPermissions.data.map(permission => ({
          ...permission,
          isAssigned: userPermissionIds.has(permission.id)
        }));

        if (assignmentFilter === "assigned") {
          combinedData = combinedData.filter(p => p.isAssigned);
        } else if (assignmentFilter === "unassigned") {
          combinedData = combinedData.filter(p => !p.isAssigned);
        }

        setSearchResults({
          data: combinedData,
          pagination: allPermissions.pagination
        });
      }
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleSearchChange = useCallback(
    (searchFormValues: UserPermissionSearchFormValues) => {
      fetchSearchResult(
        searchResults.pagination,
        searchFormValues.search,
        searchFormValues.categoryIds,
        searchFormValues.includeSubcategories,
        searchFormValues.assignmentFilter
      );
    },
    [searchResults.pagination]
  );

  const handleOpenBulkEditModal = useCallback(() => {
    setShowBulkEditModal(true);
  }, []);

  const handleBulkEditSuccess = useCallback(() => {
    fetchSearchResult(searchResults.pagination);
    setSelectedRows([]);
    setSelectedPermissionsMap(new Map());
    setShowBulkEditModal(false);
  }, [searchResults.pagination]);

  const fetchAllAndSelect = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const [allPermissions, userPermissions] = await Promise.all([
        getPermissionsAPI({ ...searchResults.pagination, pageNumber: 1, pageSize: 9999 }),
        getUserPermissionsAPI(id)
      ]);

      const userPermissionIds = new Set(userPermissions.map(up => up.id));

      const allPermissionsWithAssignment: PermissionWithAssignment[] = allPermissions.data.map(permission => ({
        ...permission,
        isAssigned: userPermissionIds.has(permission.id)
      }));

      const allIds = allPermissionsWithAssignment.map(p => p.id);
      const newMap = new Map<string, PermissionWithAssignment>();
      allPermissionsWithAssignment.forEach(p => newMap.set(p.id, p));

      setSelectedRows(allIds);
      setSelectedPermissionsMap(newMap);
    } catch (error) {
      console.error("Error fetching all permissions:", error);
    } finally {
      setIsLoading(false);
    }
  }, [id, searchResults.pagination]);

  const handleSelectedChange = (id: string | string[]) => {
    if (Array.isArray(id)) {
      if (id.length === 0) {
        setSelectedRows([]);
        setSelectedPermissionsMap(new Map());
      } else {
        fetchAllAndSelect();
      }
      return;
    }
    setSelectedRows(prev => {
      const isAlreadySelected = prev.includes(id);
      if (isAlreadySelected) {
        return prev.filter(r => r !== id);
      }
      return [...prev, id];
    });
    setSelectedPermissionsMap(prev => {
      const newMap = new Map(prev);
      if (newMap.has(id)) {
        newMap.delete(id);
      } else {
        const permission = searchResults.data.find(p => p.id === id);
        if (permission) {
          newMap.set(id, permission);
        }
      }
      return newMap;
    });
  };

  const handlePaginationChange = (newPagination: Pagination) => {
    fetchSearchResult(newPagination);
  };

  const selectedPermissions = useMemo(() => {
    return Array.from(selectedPermissionsMap.values());
  }, [selectedPermissionsMap]);

  if (!id) return <>REDIRECT TODO</>;

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md">
          <PageHeader>
            <PageTitle
              title={USER_PERMISSION_SEARCH_PAGE_TITLE.text}
              icon={USER_PERMISSION_SEARCH_PAGE_TITLE.icon}
              iconColor={USER_PERMISSION_SEARCH_PAGE_TITLE.iconColor}
            />
            <Breadcrumb />
          </PageHeader>
          <TabMenu userId={id} />
        </div>
        <div className="px-4 pt-4">
          <UserPermissionSearchFilters
            onSearchChange={handleSearchChange}
            onBulkEdit={handleOpenBulkEditModal}
            selectedCount={selectedRows.length}
            categories={categories}
          />
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults message="Unesite pravo klikom na 'Dodaj pravo'" />
          ) : (
            <UserPermissionSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              selectableRows={true}
           
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
            />
          )}
        </div>
      </div>  
      <UserPermissionBulkEditModal
        show={showBulkEditModal}
        userId={id}
        selectedPermissions={selectedPermissions}
        onClose={() => setShowBulkEditModal(false)}
        onSuccess={handleBulkEditSuccess}
      />
    </>
  );
};

UserPermissionSearchPage.route = Routes.UserPermissionSearchPageRouter;

export default UserPermissionSearchPage;
